import { Text } from "@/components/ui/baseUi/text"
import type { ScoreboardRankingEntity } from "@/modules/scores/scoreboard/entities/scoreboardRankingEntity"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import ScoreEmpty from "@images/empty/score_empty.svg"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { word } from "@modules/core/utils/i18n"
import { ScoreboardDetailsCard } from "@modules/scores/scoreboard/components/scoreboardDetailsCard"
import { useMyRanking, useScoreboard } from "@modules/scores/scoreboard/hooks/useScoreboard"
import { ScoreDifficultyFilterEnum, type ExerciseScoreCategory } from "@modules/scores/scoreTypes"
import { EmptyView } from "@modules/ui/components/emptyView"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { useCallback, useEffect, useMemo, useRef } from "react"

type ScoreboardListViewProps = {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | BoxSessionDetailsEntity
  selectedExercise: SessionBlockExerciseEntity
  selectedDifficulty: ScoreDifficultyFilterEnum
  categorySelected: ExerciseScoreCategory
} & React.ComponentProps<"div">

export function ScoreboardListView({
  session,
  selectedExercise,
  selectedDifficulty,
  categorySelected,
  ...props
}: ScoreboardListViewProps) {
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const {
    isLoading: scoreboardLoading,
    data: scoreboard,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useScoreboard(
    "programId" in session ? session.programId : "",
    session.id,
    selectedExercise?.id,
    selectedDifficulty,
    categorySelected,
  )

  const { data: myRanking } = useMyRanking(
    "programId" in session ? session.programId : "",
    session.id,
    selectedExercise?.id,
    selectedDifficulty,
    categorySelected,
  )

  const updateUserScorePosition = (
    userScores: ScoreboardRankingEntity[],
    myRankingId: string,
  ): ScoreboardRankingEntity[] => {
    const targetUserIndex = userScores.findIndex((user) => user.userId === myRankingId)
    if (targetUserIndex !== -1) {
      const targetUser = userScores[targetUserIndex]
      userScores.splice(targetUserIndex, 1)
      userScores.unshift(targetUser!)
    }
    return userScores
  }

  const scoreboardOrder = useMemo(() => {
    if (scoreboard && myRanking?.position && myRanking.position > 4) {
      const updateScoreboard = updateUserScorePosition([...scoreboard.content], myRanking.userId)
      return updateScoreboard
    } else return scoreboard?.content
  }, [scoreboard, myRanking])

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection)

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [handleIntersection])

  if (scoreboardLoading) return <LoadingSpinner />

  if (scoreboard?.content.length === 0)
    return (
      <EmptyView
        image={{ src: ScoreEmpty }}
        title={word("scoreboard.empty.title")}
        description={word("scoreboard.empty.description")}
        flexGrow={false}
        className="flex justify-center bg-surface-foreground p-6"
        textAlign="center"
        disposition="column"
      />
    )

  return (
    <div className="" id="scoreboard-container" {...props}>
      {scoreboardOrder
        ?.filter((userScore) => !!userScore.score && userScore.score.valueDecimal !== null)
        .map((userScore, i) => (
          <ScoreboardDetailsCard
            key={i}
            userScore={userScore}
            myRanking={myRanking ?? null}
            session={session}
            selectedExercise={selectedExercise}
          />
        ))}
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="flex w-full flex-col items-center justify-center">
          <Text fontStyle="BM">{word("global.loading")}</Text>
          <LoadingSpinner className="h-auto" />
        </div>
      )}
    </div>
  )
}
